import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import BigNumber from 'bignumber.js';
import { OLstToken, OLstvToken } from 'modules/api';
import { SpriteIcon } from 'modules/common/components/SpriteIcon';
import { TxExplorerLink } from 'modules/common/components/TxExplorerLink/TxExplorerLink';
import { DECIMAL_PLACES_BTC } from 'modules/common/const';
import { InfoIcon, lbtcSpriteIcon } from 'modules/common/icons';
import { useGlobalTranslation, useTranslation } from 'modules/i18n';
import { PropsWithChildren, useState } from 'react';
import { useWithdrawalForm } from '../../hooks/useWithdrawalForm';
import { translation } from './translation';
import { useIsWithdrawalInProgress } from '../../hooks/useIsWithdrawalInProgress';
import { useWithdrawalTimeLeft } from '../../hooks/useWithdrawalTimeLeft';
import { useShareValue } from 'modules/withdrawal/hooks/useShareValue';
import { Link } from 'react-router-dom';
import { NetworkIcon } from 'modules/common/components/NetworkIcon';
import { CancelWithdrawalModal } from 'modules/withdrawal/components/CancelWithdrawalModal';

interface IWithdrawalInProgressProps extends PropsWithChildren {}

export const WithdrawalInProgress = ({
  children,
}: IWithdrawalInProgressProps): JSX.Element => {
  const {
    form: { watch },
  } = useWithdrawalForm();
  const chain = watch('chain');
  const { keys: globalKeys } = useGlobalTranslation();

  const { transactionHash, amount, deadline, isLoading, chainId } =
    useIsWithdrawalInProgress();
  const { data: shareValue, isLoading: isShareValueLoading } = useShareValue();

  const { t, keys } = useTranslation(translation);

  const isWithdrawalInProgress = !!transactionHash && !!amount;

  const timeLeft = useWithdrawalTimeLeft(deadline);

  const [isModalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  if (isLoading || isShareValueLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" height={300}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!isWithdrawalInProgress) {
    return <>{children}</>;
  }

  const lbtcvValue = new BigNumber(amount)
    .decimalPlaces(DECIMAL_PLACES_BTC)
    .toFormat();

  const lbtcValue = new BigNumber(amount)
    .multipliedBy(shareValue || 1)
    .decimalPlaces(DECIMAL_PLACES_BTC)
    .toFormat();

  return (
    <>
      <Stack gap={3}>
        <Typography variant="h6">{t(keys.title)}</Typography>

        <Stack gap={1.5}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography color="text.secondary">
              {t(keys.youWithdraw)}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Typography fontWeight="500">
                {t('unit.tokenValue', {
                  token: OLstvToken.sonicLBTCv,
                  value: lbtcvValue,
                })}
              </Typography>

              <SpriteIcon src={lbtcSpriteIcon} />
            </Stack>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography color="text.secondary">{t(keys.youReceive)}</Typography>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Typography fontWeight="500">
                {t('unit.tokenValue', {
                  token: OLstToken.LBTC,
                  value: lbtcValue,
                })}
              </Typography>

              <SpriteIcon src={lbtcSpriteIcon} />
            </Stack>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography color="text.secondary">{t(keys.network)}</Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <NetworkIcon chainId={chainId} sx={{ fontSize: 20, mr: 1.25 }} />
              {chainId && t(globalKeys.chain[chainId])}
            </Stack>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography color="text.secondary">
              {t(keys.transactionId)}
            </Typography>
            <TxExplorerLink
              txId={transactionHash}
              network={chain}
              buttonProps={{ sx: { p: 0 } }}
            />
          </Stack>
        </Stack>

        <Alert iconMapping={{ success: <InfoIcon width={24} height={24} /> }}>
          {t(keys.withdrawalWillTake, {
            value: timeLeft.value,
            unit: timeLeft.unit,
          })}
        </Alert>

        <Button
          fullWidth
          variant="contained"
          size="large"
          color="secondary"
          onClick={handleOpenModal}
        >
          {t(keys.cancelWithdrawal)}
        </Button>

        <Button
          fullWidth
          variant="contained"
          size="large"
          component={Link}
          to="https://www.lombard.finance/app/vaults/"
        >
          {t(keys.goToVaults)}
        </Button>
      </Stack>

      <CancelWithdrawalModal open={isModalOpen} onClose={handleCloseModal} />
    </>
  );
};
